<template>
  <!-- Teleported to body: it opens over the bottom sheets (z 1000), which #app (its own stacking context) cannot. -->
  <Teleport to="body">
  <div v-if="state.open" class="suc__overlay" @click.self="onCancel">
    <div class="suc__sheet">
      <div class="suc__head">
        <div class="suc__title">{{ t('stepup.title') }}</div>
        <button class="suc__x" :aria-label="t('stepup.cancel')" @click="onCancel">✕</button>
      </div>

      <!-- Factor picker -->
      <template v-if="mode === 'choose'">
        <p class="suc__sub">{{ t('stepup.subtitle') }}</p>
        <div class="suc__options">
          <!-- Признаки для проверок: способ подтверждения нельзя выбирать по подписи — она переводится
               на три языка, и тест на другом языке молча кликал бы мимо. -->
          <button v-if="avail.passkey" class="suc__opt" data-testid="stepup-passkey" :disabled="busy" @click="doPasskey">
            {{ t('stepup.passkey') }}
          </button>
          <button v-if="avail.appPin" class="suc__opt" data-testid="stepup-pin" :disabled="busy" @click="openCode('pin')">
            {{ t('stepup.appPin') }}
          </button>
          <button v-if="avail.email" class="suc__opt" data-testid="stepup-email" :disabled="busy" @click="startOtp">
            {{ t('stepup.email') }}
          </button>
        </div>
        <!-- The key export: a PIN / passkey of this device counts once enrolled 3 days ago — say when. -->
        <p v-for="p in pendingNotes" :key="p.factor" class="suc__note" :data-testid="`stepup-pending-${p.factor}`">
          {{ p.text }}
        </p>
        <p v-if="error" class="suc__error">{{ error }}</p>
        <button v-if="supportCodeAccepted(state.purpose)" class="suc__link" @click="mode = 'support'">{{ t('stepup.haveCode') }}</button>
      </template>

      <!-- Code entry (app-PIN or email OTP) — masked dots + keypad, never shows the digits. -->
      <template v-else-if="mode === 'pin' || mode === 'otp'">
        <p class="suc__sub">{{ mode === 'pin' ? t('stepup.pinPrompt') : t('stepup.otpPrompt') }}</p>
        <div class="suc__dots" :class="{ 'suc__dots--error': shake }">
          <span v-for="i in 6" :key="i" class="suc__dot" :class="{ 'suc__dot--on': i <= code.length }" />
        </div>
        <p class="suc__err-line">{{ error }}</p>
        <PinKeypad :disabled="busy" @digit="onDigit" @backspace="onBackspace" />
        <button class="suc__link" :disabled="busy" @click="backToChoose">{{ t('stepup.cancel') }}</button>
      </template>

      <!-- Operator-assisted support code (§9) — the code is the grant: XXXX-XXXX, read over the phone. -->
      <template v-else>
        <p class="suc__sub">{{ t('stepup.codePrompt') }}</p>
        <input ref="codeInput" :value="supportCode" class="suc__input suc__input--code" data-testid="stepup-support-code"
               placeholder="XXXX-XXXX" maxlength="9" inputmode="text" autocapitalize="characters" spellcheck="false"
               autocomplete="off" @input="onSupportInput" @keyup.enter="submitSupport" />
        <div class="suc__actions">
          <button class="suc__btn suc__btn--ghost" :disabled="busy" @click="backToChoose">{{ t('stepup.cancel') }}</button>
          <button class="suc__btn" :disabled="busy || !supportCodeComplete(supportCode)" @click="submitSupport">
            {{ t('stepup.submit') }}
          </button>
        </div>
      </template>
    </div>
  </div>
  </Teleport>
</template>

<script setup lang="ts">
import { computed, nextTick, reactive, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStepUp } from '@/composables/useStepUp';
import { formatSupportCode, stepUpApi, supportCodeAccepted, supportCodeComplete } from '@/api/stepup';
import { stepUpForReveal, passkeyEnrolledOnDevice, forgetPasskeyDevice } from '@/api/webauthn';
import { devicePinApi } from '@/api/devicePin';
import PinKeypad from '@/components/ui/controls/PinKeypad.vue';

const { t } = useI18n();
const { state, complete, cancel } = useStepUp();

type Mode = 'choose' | 'pin' | 'otp' | 'support';
const mode = ref<Mode>('choose');
const busy = ref(false);
const error = ref('');
const shake = ref(false);
const code = ref('');                 // masked PIN/OTP digits — never rendered, only dot count
const supportCode = ref('');
const otpRequestId = ref('');
const codeInput = ref<HTMLInputElement | null>(null);
const avail = reactive({ passkey: false, appPin: false, email: false });

const DAY_MS = 86_400_000;

/** «PIN станет доступен для этого через N дн.» for each pending factor (whole days, rounded up, at least 1). */
const pendingNotes = computed(() => state.pending.map((p) => {
  const days = Math.max(1, Math.ceil((Date.parse(p.availableAt) - Date.now()) / DAY_MS));
  const key = p.factor === 'PASSKEY' ? 'stepup.pendingPasskey' : 'stepup.pendingPin';
  return { factor: p.factor, text: t(key, { days }) };
}));

// Recompute available factors each time the sheet opens.
watch(() => state.open, async (open) => {
  if (!open) return;
  mode.value = 'choose';
  reset();
  // Offer Face ID only when a passkey is enrolled ON THIS device. The server listing PASSKEY in
  // `state.factors` just means the customer has one somewhere (e.g. their phone); firing WebAuthn on
  // a device with no local credential pops Chrome's "no passkeys found" dialog. `passkeyEnrolledOnDevice()`
  // is the this-device signal — otherwise we show only app-PIN / email code here.
  avail.passkey = state.factors.includes('PASSKEY') && passkeyEnrolledOnDevice();
  avail.appPin = state.factors.includes('APP_PIN') && (await devicePinSet());
  avail.email = state.factors.includes('EMAIL_OTP');
  // The caller already tries Face ID first before opening the chooser, so we present buttons here.
});

function reset() {
  error.value = '';
  code.value = '';
  supportCode.value = '';
  otpRequestId.value = '';
  shake.value = false;
}

async function devicePinSet(): Promise<boolean> {
  try {
    return (await devicePinApi.status()).enabled;
  } catch {
    return false;
  }
}

function openCode(m: 'pin' | 'otp') {
  code.value = '';
  error.value = '';
  mode.value = m;
}

// ─── masked keypad ────────────────────────────────────────
function onDigit(d: string) {
  if (busy.value || code.value.length >= 6) return;
  error.value = '';
  code.value += d;
  if (code.value.length === 6) void submitCode();
}
function onBackspace() {
  if (busy.value) return;
  error.value = '';
  code.value = code.value.slice(0, -1);
}

async function submitCode() {
  busy.value = true;
  try {
    const res = mode.value === 'pin'
      ? await stepUpApi.appPin(code.value, state.purpose)
      : await stepUpApi.otpVerify(otpRequestId.value, code.value, state.purpose);
    if (res.ok && res.grant) { complete(res.grant); return; }
    failShake();
  } catch {
    failShake();
  } finally {
    busy.value = false;
  }
}

function failShake() {
  error.value = t('stepup.error');
  shake.value = true;
  code.value = '';
  setTimeout(() => { shake.value = false; }, 450);
}

// ─── passkey ──────────────────────────────────────────────
async function doPasskey() {
  busy.value = true;
  error.value = '';
  try {
    complete(await stepUpForReveal(state.purpose));
  } catch {
    // The device flag said a passkey lived here but the ceremony found none (removed / never synced) —
    // forget it so we stop offering Face ID, hide the button now, and let the client pick another factor.
    forgetPasskeyDevice();
    avail.passkey = false;
    error.value = t('stepup.error');
  } finally {
    busy.value = false;
  }
}

// ─── email OTP request → switch to masked code entry ──────
async function startOtp() {
  busy.value = true;
  error.value = '';
  try {
    otpRequestId.value = (await stepUpApi.otpRequest()).requestId;
    openCode('otp');
  } catch {
    error.value = t('stepup.error');
  } finally {
    busy.value = false;
  }
}

// ─── operator support code (the code IS the grant) ────────
function onSupportInput(e: Event) {
  const input = e.target as HTMLInputElement;
  supportCode.value = formatSupportCode(input.value);
  input.value = supportCode.value;
}

function submitSupport() {
  if (supportCodeComplete(supportCode.value)) complete(supportCode.value);
}

function backToChoose() {
  mode.value = 'choose';
  reset();
}

function onCancel() {
  cancel();
}

watch(mode, async (m) => {
  if (m === 'support') {
    await nextTick();
    codeInput.value?.focus();
  }
});
</script>

<style scoped lang="scss">
.suc__overlay {
  position: fixed; inset: 0; z-index: 2000; display: grid; place-items: center;
  background: var(--scrim); padding: 20px;
}
.suc__sheet {
  width: 100%; max-width: 360px; border-radius: 20px; padding: 24px;
  background: var(--ds-surface); box-shadow: var(--shadow-dialog, var(--shadow-surface)); color: var(--text);
  display: flex; flex-direction: column; gap: 14px;
}
.suc__head { display: flex; align-items: center; justify-content: space-between; }
.suc__title { font-family: var(--font-heading); font-size: 24px; font-weight: 400; letter-spacing: -0.01em; }
.suc__x { background: none; border: none; color: var(--muted); font-size: 16px; cursor: pointer; }
.suc__sub { font-size: 14px; color: var(--muted); margin: 0; }
.suc__options { display: flex; flex-direction: column; gap: 10px; }
.suc__opt {
  width: 100%; border: 1px solid var(--line); border-radius: 14px; padding: 14px;
  background: var(--cream); color: var(--text); font: inherit; font-weight: 500; cursor: pointer;
  &:hover:not(:disabled) { background: var(--soft); }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
}

/* masked dots (identical to AppLock) */
.suc__dots { display: flex; gap: 16px; justify-content: center; margin: 6px 0 2px; }
.suc__dots--error { animation: suc-shake 0.4s; }
.suc__dot {
  width: 10px; height: 10px; border-radius: 50%; box-sizing: border-box;
  border: 1px solid var(--pin-dot); transition: background 0.15s, transform 0.15s, border-color 0.15s;
}
.suc__dot--on { background: var(--bronze); border-color: var(--bronze); transform: scale(1.08); }
.suc__dots--error .suc__dot { background: var(--pin-error); border-color: var(--pin-error); }
.suc__err-line { color: var(--pin-error); font-size: 12.5px; margin: 0; min-height: 16px; text-align: center; }

.suc__input {
  border-radius: 12px; background: var(--cream); border: 1px solid var(--field-border);
  color: var(--text); padding: 12px 14px; font-size: 16px; letter-spacing: 1px; text-align: center;
  font-family: var(--font-mono);
}
.suc__input--code { font-size: 22px; letter-spacing: 3px; }
.suc__link {
  background: none; border: none; color: var(--bronze-text); font: inherit; font-size: 13px;
  cursor: pointer; text-decoration: underline; align-self: center;
  &:disabled { opacity: 0.5; cursor: not-allowed; }
}
.suc__error { color: var(--red); font-size: 13px; margin: 0; }
.suc__note { color: var(--muted); font-size: 13px; margin: 0; }
.suc__actions { display: flex; gap: 10px; }
.suc__btn {
  flex: 1; border: none; border-radius: 14px; padding: 13px; cursor: pointer; font-weight: 500;
  background: var(--bronze-strong); color: var(--cream); box-shadow: var(--shadow-primary);
  &:disabled { opacity: 0.5; cursor: not-allowed; }
  &--ghost { background: transparent; color: var(--text); border: 1px solid var(--line); box-shadow: none; }
}

@keyframes suc-shake {
  0%, 100% { transform: translateX(0); }
  20% { transform: translateX(-9px); }
  40% { transform: translateX(8px); }
  60% { transform: translateX(-6px); }
  80% { transform: translateX(4px); }
}
</style>
